import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';

import { DeviceState } from '@marsinet/commons';
import { apiServices } from 'apps/marsinet/src/app/services/apiServices';
import { useToast } from 'apps/marsinet/src/app/context/ToastProvider';
import { normalizeApiError } from 'apps/marsinet/src/app/errors/normalize-api-error';
import { UpdateDeviceStateDto } from 'apps/marsinet/src/app/typescript-axios';

export interface ModalSuggestDeviceStateProps {
    visible: boolean;
    onHide: () => void;
    deviceId: string;
    currentState?: DeviceState;
    allowedStates: DeviceState[];
    onSaved?: () => void;
}

/** Muestra el estado actual del dispositivo y permite proponer uno de los estados permitidos. */
const ModalSuggestDeviceState: React.FC<ModalSuggestDeviceStateProps> = ({
    visible,
    onHide,
    deviceId,
    currentState,
    allowedStates,
    onSaved,
}) => {
    const { t } = useTranslation();
    const { showToast: show } = useToast();
    const [selected, setSelected] = useState<DeviceState | null>(null);
    const [saving, setSaving] = useState<boolean>(false);
    const statusDefinition = selected ? t(`pages.devices.statusInfo.${selected}.definition`, { defaultValue: '' }) : '';
    const statusResponsible = selected ? t(`pages.devices.statusInfo.${selected}.responsible`, { defaultValue: '' }) : '';
    const showStatusInfo = !!(statusDefinition || statusResponsible);

    useEffect(() => {
        if (visible) setSelected(allowedStates.length === 1 ? allowedStates[0] : null);
    }, [visible]);

    // Guarda el estado seleccionado y notifica el resultado mediante un mensaje.
    const handleSave = async () => {
        if (!selected) return;
        setSaving(true);
        try {
            await apiServices.devicesApi.devicesControllerUpdateDeviceState(
                {
                    device_state:
                        selected as unknown as UpdateDeviceStateDto['device_state'],
                },
                deviceId
            );
            show(
                'success',
                t('messages.success'),
                t('messages.devices.update_device')
            );
            onSaved?.();
            onHide();
        } catch (error: unknown) {
            const normalizedError = normalizeApiError(error);
            show(
                'error',
                t('messages.error'),
                t(normalizedError.messageKey, {
                    ...normalizedError.params,
                    defaultValue: t('controlErrors.UNKNOWN_ERROR'),
                })
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            header={t('pages.devices.stateSuggestion.title')}
            visible={visible}
            onHide={onHide}
            className="mx-2 md:w-12 lg:w-5"
            breakpoints={{ '960px': '75vw', '641px': '100vw' }}
        >
            <p className="mt-0 mb-2">
                {t('pages.devices.stateSuggestion.currentState')}:{' '}
                <b>{currentState ? t(`pages.devices.statusInfo.${currentState}.name`) : '-'}</b>
            </p>
            <p className="mt-0 mb-4">{t('pages.devices.stateSuggestion.question')}</p>

            <div className="p-fluid mb-5">
                <Dropdown
                    value={selected}
                    options={allowedStates.map((status) => ({
                        label: t(`pages.devices.statusInfo.${status}.name`),
                        value: status,
                    }))}
                    onChange={(e) => setSelected(e.value)}
                    placeholder={t('pages.devices.selectStatus')}
                />
            </div>

            {showStatusInfo && (
                <div className="surface-50 border-round-lg border-1 surface-border p-3 mb-5">
                    <div className="flex align-items-center mb-2">
                        <i className="pi pi-info-circle text-primary mr-2"></i>
                        <span className="font-bold text-900">
                            {t(`pages.devices.statusInfo.${selected}.name`)}
                        </span>
                    </div>
                    {statusDefinition && (
                        <p className="m-0 mb-3 text-700 line-height-3">{statusDefinition}</p>
                    )}
                    {statusResponsible && (
                        <div className="flex align-items-center text-600 text-sm">
                            <i className="pi pi-user mr-2"></i>
                            <span>
                                {t('pages.devices.statusResponsible')}: <b>{statusResponsible}</b>
                            </span>
                        </div>
                    )}
                </div>
            )}

            <div className="flex align-items-center justify-content-end gap-2">
                <Button
                    type="button"
                    severity="secondary"
                    outlined
                    label={t('pages.devices.stateSuggestion.doNothing')}
                    onClick={onHide}
                />
                <Button
                    type="button"
                    severity="secondary"
                    icon="pi pi-save"
                    label={t('pages.devices.stateSuggestion.save')}
                    className="p-button-raised"
                    onClick={handleSave}
                    disabled={!selected}
                    loading={saving}
                />
            </div>
        </Dialog>
    );
};

export default ModalSuggestDeviceState;